import type {Where} from 'payload'

import type {Industry} from '~/types/legacy'
import type {Populated} from '~/types/populated'
import {resolveRelation, resolveRelations, resolveMediaUrl} from '~/utils/relations'
import {extractDocs} from '~/utils/transform-payload'

export function useProjects() {
  const sdk = usePayloadSdk()
  const route = useRoute()

  const fetchBySlug = async (slug: string) => {
    const where: Where = {
      slug: {equals: slug},
    }

    const response = await sdk.find({
      collection: 'projects',
      depth: 2,
      where,
      limit: 1,
      draft: !!route.query.preview,
    })

    const projects = extractDocs(response)
    const project = projects[0]

    if (!project) {
      throw createError({
        statusCode: 404,
        statusMessage: 'Project not found',
      })
    }

    const allProjectsResponse = await sdk.find({
      collection: 'projects',
      where: {
        industries: {
          in: project.industries?.map(i => i.id) || [],
        },
      },
      draft: !!route.query.preview,
      sort: '-createdAt',
      limit: 1000,
    })

    const allProjects = extractDocs(allProjectsResponse)
    const currentIndex = allProjects.findIndex(p => p.slug === slug)
    const previousSlug = currentIndex > 0 ? allProjects[currentIndex - 1]?.slug : undefined
    const nextSlug = currentIndex < allProjects.length - 1 ? allProjects[currentIndex + 1]?.slug : undefined

    return {
      project,
      nextSlug,
      previousSlug,
      meta: {
        title: project.title,
        description: project.description,
        imageUrl: resolveMediaUrl(project.image),
      },
    }
  }

  const fetch = async (industrySlug?: string, limit?: number) => {
    let industry: Populated<Industry> | undefined

    if (industrySlug) {
      const industryResponse = await sdk.find({
        collection: 'industries',
        where: {
          slug: {equals: industrySlug},
          _status: {equals: 'published'},
        },
        limit: 1,
      })
      const industries = extractDocs(industryResponse)
      industry = industries[0]
    }

    const conditions: Where['and'] = [{_status: {equals: 'published'}}]
    if (industry?.id) {
      conditions.push({
        industries: {
          in: [industry.id],
        },
      })
    }

    console.log(conditions)

    const response = await sdk.find({
      collection: 'projects',
      depth: 2,
      where: {and: conditions},
      sort: '-createdAt',
      limit: limit || 1000,
    })

    const projects = extractDocs(response)

    return {
      ...(industry && {industry}),
      projects: projects.map(item => ({
        ...item,
        industries: resolveRelations(item.industries).map(ind => ind.name),
        industriesSlugs: resolveRelations(item.industries).map(ind => ind.slug),
        countryName: resolveRelation(item.country)?.countryName,
      })),
    }
  }

  return {
    fetch,
    fetchBySlug,
  }
}
